import { useMutation, useApolloClient } from "@apollo/client";
import { size, isEmpty, chunk } from "lodash";
import moment from "moment";
import { useCallback, useState } from "react";

import { bulkMoveIndividuals } from "src/allocation/graphql/allocation.graphql";
import {
  getMemberships,
  mutateMemberships,
} from "src/queries/memberships.graphql";
import { MEMBERSHIP_DATE_FORMAT } from "src/util/memberships";
import { getEndDateFromStartDate } from "src/components/PersonDetailPage/PersonMemberships/util/membership";
import { getMemberCountDisplay } from "src/util/search";

import {
  MEMBERSHIP_EVENTS,
  MEMBERSHIP_CHANGE_TYPES,
} from "src/components/Person/events";
import eventEmitter from "src/services/eventEmitter";
import { MAX_MOVE_LIMIT, STATUS } from "../consts";
import { getAllocationsFromMembers } from "../utils/index";

// Batch size for API calls (used for both move and end membership operations)
const BATCH_SIZE = 10;

/**
 * Helper to set error state with status
 */
const createErrorState = (message) => ({
  status: { status: STATUS.ERROR, message },
  error: new Error(message),
});

/**
 * Helper to create progress status
 */
const createProgressStatus = (action, count) => ({
  status: STATUS.PROGRESS,
  message: `${action} ${getMemberCountDisplay(count)} in progress...`,
});

/**
 * Helper to create success status
 */
const createSuccessStatus = (action, count, targetName) => ({
  status: STATUS.SUCCESS,
  message: `${getMemberCountDisplay(
    count
  )} ${action} successfully ${targetName}`,
});

const useBulkMoveMembers = ({
  team,
  selectedPeople,
  allocationProject,
  onHideModal = () => {},
}) => {
  const client = useApolloClient();
  const [status, setStatus] = useState(null);
  const [bulkMoveMutation] = useMutation(bulkMoveIndividuals);
  const [mutateMembershipsMutation] = useMutation(mutateMemberships);
  const [bulkMoveSuccess, setBulkMoveSuccess] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState(selectedPeople || {});
  const [targetGroupOption, setTargetGroupOption] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleCloseModal = useCallback(() => {
    if (isProcessing) {
      return;
    }
    setStatus(null);
    setError(null);
    setSelectedMembers({});
    setIsProcessing(false);
    setTargetGroupOption(null);
    setBulkMoveSuccess(false);
    onHideModal();
  }, [isProcessing, onHideModal]);

  const handleSelectPerson = useCallback((person) => {
    setSelectedMembers((prev) => {
      const updated = { ...prev };
      if (updated[person.aggregateId]) {
        delete updated[person.aggregateId];
      } else {
        updated[person.aggregateId] = person;
      }
      return updated;
    });
  }, []);

  /**
   * Validates selected members before bulk operations
   * @returns {string|null} Error message if validation fails, null if valid
   */
  const validateSelection = useCallback(
    (action) => {
      if (isEmpty(selectedMembers)) {
        return `No members are selected to be ${action}`;
      }
      if (size(selectedMembers) > MAX_MOVE_LIMIT) {
        return `Too many members selected (selected: ${size(
          selectedMembers
        )}, limit: ${MAX_MOVE_LIMIT}).`;
      }
      return null;
    },
    [selectedMembers]
  );

  /**
   * Fetches memberships for multiple persons for current team.
   * We need to fetch the memberships to get the currentCompositeId and startDate
   * which are required by the mutateMemberships mutation to properly end the membership.
   * The selected person object doesn't include membership details, only basic person info.
   */
  const fetchPersonMemberships = useCallback(
    async (personIds) => {
      if (!team?.id || isEmpty(personIds)) {
        return [];
      }

      // Only pass in personIds and groupIds to load the memberships in the current team.
      const { data } = await client.query({
        query: getMemberships,
        variables: {
          input: {
            personIds,
            groupIds: [team.id],
            statuses: ["ALLOCATED"],
          },
          personMembershipsInput: {},
          includeGroup: false,
          includePerson: false,
        },
        fetchPolicy: "network-only",
      });

      return data?.result?.memberships || [];
    },
    [client, team]
  );

  /**
   * Emits membership change event and clears selection.
   * Uses BULK_MOVE type by default as the cache updater expects personIds array
   * for bulk operations (both move and remove).
   * @param {string[]} personIds - Array of person aggregate IDs affected
   * @param {string} changeType - Type of change (defaults to BULK_MOVE)
   */
  const emitChangeAndClearSelection = useCallback(
    (personIds, changeType = MEMBERSHIP_CHANGE_TYPES.BULK_MOVE) => {
      eventEmitter.emit(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, {
        type: changeType,
        personIds,
        targetTeamId: team?.id,
      });
      setSelectedMembers({});
    },
    [team]
  );

  const handleBulkMove = async () => {
    const count = size(selectedMembers);
    setBulkMoveSuccess(false);

    // Validate target group
    if (!targetGroupOption?.group) {
      const errorState = createErrorState("Target group is not selected");
      setStatus(errorState.status);
      setError(errorState.error);
      return;
    }

    // Validate selection
    const validationError = validateSelection("moved");
    if (validationError) {
      const errorState = createErrorState(validationError);
      setStatus(errorState.status);
      setError(errorState.error);
      return;
    }

    try {
      setError(null);
      setIsProcessing(true);
      setStatus(createProgressStatus("Moving", count));

      const allocations = getAllocationsFromMembers(selectedMembers);
      const chunks = chunk(allocations, BATCH_SIZE);

      for (const currentChunk of chunks) {
        // eslint-disable-next-line no-await-in-loop
        await bulkMoveMutation({
          variables: {
            input: {
              allocations: currentChunk,
              fromGroupId: team?.id,
              toGroupId: targetGroupOption.group.id,
              allocationProjectId: allocationProject?.id,
            },
          },
        });
      }

      setBulkMoveSuccess(true);
      setStatus(
        createSuccessStatus(
          "moved",
          count,
          `to ${targetGroupOption.group.name}`
        )
      );
      emitChangeAndClearSelection(Object.keys(selectedMembers));
    } catch (err) {
      setError(err);
      setBulkMoveSuccess(false);
      setStatus({
        status: STATUS.ERROR,
        message: `Failed moving members: ${err?.message}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkEndMembership = async () => {
    const count = size(selectedMembers);

    // Validate selection
    const validationError = validateSelection("ended");
    if (validationError) {
      const errorState = createErrorState(validationError);
      setStatus(errorState.status);
      setError(errorState.error);
      return;
    }

    try {
      setError(null);
      setIsProcessing(true);
      setStatus(createProgressStatus("Ending memberships for", count));

      const personIds = Object.keys(selectedMembers);

      // Fetch memberships for all selected persons in a single query
      const validMemberships = await fetchPersonMemberships(personIds);

      if (isEmpty(validMemberships)) {
        throw new Error("No memberships found for selected members");
      }

      // Process end memberships in batches by updating with an end date
      const membershipChunks = chunk(validMemberships, BATCH_SIZE);

      for (const currentBatch of membershipChunks) {
        const updatedMemberships = currentBatch.map((membership) => {
          const { currentCompositeId, fte, startDate } = membership;
          const endDate = getEndDateFromStartDate(moment(startDate));

          return {
            currentCompositeId,
            startDate,
            endDate: endDate.format(MEMBERSHIP_DATE_FORMAT),
            fte,
          };
        });

        await mutateMembershipsMutation({
          variables: {
            input: {
              updatedMemberships,
            },
            searchInput: {
              groupKinds: ["team"],
              statuses: ["ALLOCATED"],
            },
          },
        });
      }

      // Use actual count and person IDs from valid memberships processed
      const processedCount = validMemberships.length;
      const processedPersonIds = validMemberships.map((m) => m.personId);

      setStatus(
        createSuccessStatus(
          "memberships ended",
          processedCount,
          `from ${team?.name}`
        )
      );
      emitChangeAndClearSelection(processedPersonIds);
    } catch (err) {
      setError(err);
      setStatus({
        status: STATUS.ERROR,
        message: `Failed ending memberships: ${err?.message}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handleSelectPerson,
    handleBulkMove,
    handleBulkEndMembership,
    isProcessing,
    error,
    setTargetGroupOption,
    targetGroupOption,
    bulkMoveSuccess,
    selectedMembers,
    setSelectedMembers,
    handleCloseModal,
    status,
    setStatus,
  };
};

export default useBulkMoveMembers;
